import {
  individualAssuranceCancel,
  individualAssuranceStart,
  supervisedAssuranceAuthenticatorVerify,
  supervisedAssuranceCancel,
  supervisedAssuranceStart,
} from '@api/selfServiceServices';
import type { VerificationChannels } from '@api/types';
import EpcsVerifyIdImg from '@assets/side/epcs_verify_id.svg';
import EpcsVerifyIidImg from '@assets/side/epcs_verify_iid.svg';
import { useSession } from '@common/authorization/context/Session';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import { useNavigateKeepSearch } from '@hooks/useNavigateKeepSearch';
import { useSingleFlight } from '@hooks/useSingleFlight';
import AuthMethodLayout from '@selfservice/containers/AuthMethods/AuthMethodLayout';
import { generatePKCECodes, type PKCECodes } from '@selfservice/utils/pkce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { RootRoutes } from '@/index';
import EpcsIndividualFlow, {
  enterChannelStep,
  type IndividualState,
  IndividualStep,
} from './EpcsIndividualFlow';
import EpcsSidePanel from './EpcsSidePanel';
import EpcsStep1Start, { AssurancePath } from './steps/EpcsStep1Start';
import EpcsStep2VerifyIdentity from './steps/EpcsStep2VerifyIdentity';
import EpcsStep4SupervisorAuthenticate from './steps/EpcsStep4SupervisorAuthenticate';
import EpcsVerifyAuthenticator from './steps/EpcsVerifyAuthenticator';
import './EpcsSetup.less';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useIdleTimer } from 'react-idle-timer';

/** Supervised path: short window — a supervisor is physically present. */
const SUPERVISED_IDLE_TIMEOUT_MS = 30_000;
/** Individual path: 5-minute window on every screen, matching the vendor proofing deadline. */
const INDIVIDUAL_IDLE_TIMEOUT_MS = 5 * 60 * 1_000;

enum EpcsSetupStep {
  START = 'start',
  SUPERVISED_VERIFY_IDENTITY = 'supervised-verify-identity',
  SUPERVISED_VERIFY_AUTHENTICATOR = 'supervised-verify-authenticator',
  SUPERVISED_AUTHENTICATE = 'supervised-authenticate',
  INDIVIDUAL = 'individual',
}

/** Each step carries exactly the data it needs, so no screen can render without its inputs. */
type EpcsFlow =
  | { step: EpcsSetupStep.START }
  | { step: EpcsSetupStep.SUPERVISED_VERIFY_IDENTITY; assuranceId: string }
  | { step: EpcsSetupStep.SUPERVISED_VERIFY_AUTHENTICATOR; assuranceId: string }
  | { step: EpcsSetupStep.SUPERVISED_AUTHENTICATE; assuranceId: string; pkceCodes: PKCECodes }
  | {
      step: EpcsSetupStep.INDIVIDUAL;
      assuranceId: string;
      channels: VerificationChannels;
      initialState: IndividualState;
    };

/** Teardown has to cancel the assurance it actually started, on the API family that owns it. */
type StartedAssurance = { path: AssurancePath; assuranceId: string };

const TRANSLATION = 'self-service.epcs-setup';

/**
 * `location.state` is `unknown`; this narrows it at the boundary so downstream props are typed
 * and a deep-link with partial state falls through to the same `cancelFlow()` off-ramp as a
 * missing authenticator id.
 */
type EpcsRouteState = {
  authenticatorId: string;
  authenticatorShortName: string;
  authenticatorDeviceModel?: string;
};

const readRouteState = (state: unknown): EpcsRouteState | undefined => {
  if (!state || typeof state !== 'object') {
    return undefined;
  }
  const { authenticatorId, authenticatorShortName, authenticatorDeviceModel } = state as Record<
    string,
    unknown
  >;
  if (typeof authenticatorId !== 'string' || typeof authenticatorShortName !== 'string') {
    return undefined;
  }
  return {
    authenticatorId,
    authenticatorShortName,
    authenticatorDeviceModel:
      typeof authenticatorDeviceModel === 'string' ? authenticatorDeviceModel : undefined,
  };
};

const EpcsSetup = () => {
  const { tenantId } = useSession();
  const location = useLocation();
  const { t } = useTranslation();
  const { emitError } = useErrorContext();
  // Memoised so its identity is stable across renders — the effect below and the callbacks that
  // depend on it would otherwise re-run on every render, wiping the listener registration.
  const routeState = useMemo(() => readRouteState(location.state), [location.state]);
  const assuranceRef = useRef<StartedAssurance>();
  const workflowIdRef = useRef('');
  const isTornDownRef = useRef(false);
  const [flow, setFlow] = useState<EpcsFlow>({ step: EpcsSetupStep.START });
  const [individualStep, setIndividualStep] = useState<IndividualStep | undefined>();

  const navigateKeepSearch = useNavigateKeepSearch();

  const cancel = useCallback(() => {
    const started = assuranceRef.current;
    if (!started) {
      return;
    }
    assuranceRef.current = undefined;
    const cancelAssurance =
      started.path === AssurancePath.SUPERVISED
        ? supervisedAssuranceCancel
        : individualAssuranceCancel;
    void cancelAssurance({ assuranceId: started.assuranceId }, workflowIdRef.current, tenantId);
  }, [tenantId]);

  const cancelFlow = useCallback(() => {
    navigateKeepSearch(RootRoutes.SELF_SERVICE);
  }, [navigateKeepSearch]);

  const idleTimeout =
    flow.step === EpcsSetupStep.INDIVIDUAL || individualStep !== undefined
      ? INDIVIDUAL_IDLE_TIMEOUT_MS
      : SUPERVISED_IDLE_TIMEOUT_MS;
  useIdleTimer({ onIdle: cancelFlow, timeout: idleTimeout });

  // On user navigating back, page refresh, timeout, etc.
  useEffect(() => {
    if (!routeState) {
      cancelFlow();
      return;
    }
    isTornDownRef.current = false;
    workflowIdRef.current = uuidv4();
    window.addEventListener('beforeunload', cancel);
    return () => {
      isTornDownRef.current = true;
      window.removeEventListener('beforeunload', cancel);
      cancel();
    };
  }, [routeState, cancel, cancelFlow]);

  const completeFlow = useCallback(() => {
    assuranceRef.current = undefined;
    void message.success(t(`${TRANSLATION}.success-banner`));
    navigateKeepSearch(RootRoutes.SELF_SERVICE);
  }, [navigateKeepSearch, t]);

  const startAssurance = useCallback(
    async (path: AssurancePath) => {
      if (!routeState) {
        return;
      }
      const workflowId = workflowIdRef.current;
      try {
        if (path === AssurancePath.SUPERVISED) {
          const { assuranceId } = await supervisedAssuranceStart(
            { authenticatorId: routeState.authenticatorId },
            workflowId,
            tenantId,
          );
          if (isTornDownRef.current) {
            void supervisedAssuranceCancel({ assuranceId }, workflowId, tenantId);
            return;
          }
          assuranceRef.current = { path, assuranceId };
          setFlow({ step: EpcsSetupStep.SUPERVISED_VERIFY_IDENTITY, assuranceId });
          return;
        }

        const { assuranceId, verificationChannels } = await individualAssuranceStart(
          { authenticatorId: routeState.authenticatorId },
          workflowId,
          tenantId,
        );
        if (isTornDownRef.current) {
          void individualAssuranceCancel({ assuranceId }, workflowId, tenantId);
          return;
        }
        assuranceRef.current = { path, assuranceId };
        let initialState: IndividualState;
        try {
          initialState = await enterChannelStep(
            IndividualStep.EMAIL,
            verificationChannels.email,
            { assuranceId, workflowId, tenantId },
          );
        } catch (error) {
          // enterChannelStep failed after we registered the assurance — cancel it
          // before re-throwing so the outer catch can emit the error cleanly.
          void individualAssuranceCancel({ assuranceId }, workflowId, tenantId);
          assuranceRef.current = undefined;
          throw error;
        }
        if (isTornDownRef.current) {
          void individualAssuranceCancel({ assuranceId }, workflowId, tenantId);
          assuranceRef.current = undefined;
          return;
        }
        setFlow({
          step: EpcsSetupStep.INDIVIDUAL,
          assuranceId,
          channels: verificationChannels,
          initialState,
        });
      } catch (error) {
        emitError(new AppError(error as ApiError));
      }
    },
    [routeState, emitError, tenantId],
  );

  // Starting twice would leave an orphaned assurance behind that nothing can cancel.
  const [startFlow, starting] = useSingleFlight(startAssurance);

  if (!routeState) {
    return null;
  }

  switch (flow.step) {
    case EpcsSetupStep.START:
      return (
        <div className="epcs-setup">
          <AuthMethodLayout>
            <EpcsStep1Start
              deviceName={routeState.authenticatorDeviceModel || routeState.authenticatorShortName}
              busy={starting}
              onContinue={startFlow}
              cancelFlow={cancelFlow}
            />
          </AuthMethodLayout>
        </div>
      );
    case EpcsSetupStep.SUPERVISED_VERIFY_IDENTITY:
      return (
        <div className="epcs-setup">
          <AuthMethodLayout
            graphic={
              <EpcsSidePanel
                artwork={EpcsVerifyIdImg}
                caption={t(`${TRANSLATION}.verify-id.caption`)}
              />
            }
          >
            <EpcsStep2VerifyIdentity
              assuranceId={flow.assuranceId}
              tenantId={tenantId}
              workflowId={workflowIdRef.current}
              onVerified={() =>
                setFlow({
                  step: EpcsSetupStep.SUPERVISED_VERIFY_AUTHENTICATOR,
                  assuranceId: flow.assuranceId,
                })
              }
              cancelFlow={cancelFlow}
            />
          </AuthMethodLayout>
        </div>
      );
    case EpcsSetupStep.SUPERVISED_VERIFY_AUTHENTICATOR:
      return (
        <div className="epcs-setup">
          <AuthMethodLayout
            graphic={
              <EpcsSidePanel
                artwork={EpcsVerifyIidImg}
                caption={t(`${TRANSLATION}.verify-iid.caption`)}
              />
            }
          >
            <EpcsVerifyAuthenticator
              authenticatorShortName={routeState.authenticatorShortName}
              authenticatorDeviceModel={routeState.authenticatorDeviceModel}
              verify={tokenCode =>
                supervisedAssuranceAuthenticatorVerify(
                  flow.assuranceId,
                  routeState.authenticatorId,
                  tokenCode,
                  workflowIdRef.current,
                  tenantId,
                )
              }
              onVerified={async () =>
                setFlow({
                  step: EpcsSetupStep.SUPERVISED_AUTHENTICATE,
                  assuranceId: flow.assuranceId,
                  pkceCodes: await generatePKCECodes(),
                })
              }
              cancelFlow={cancelFlow}
            />
          </AuthMethodLayout>
        </div>
      );
    case EpcsSetupStep.SUPERVISED_AUTHENTICATE:
      return (
        <div className="epcs-setup">
          <EpcsStep4SupervisorAuthenticate
            assuranceId={flow.assuranceId}
            pkceCodes={flow.pkceCodes}
            tenantId={tenantId}
            workflowId={workflowIdRef.current}
            cancelFlow={cancelFlow}
            completeFlow={completeFlow}
          />
        </div>
      );
    case EpcsSetupStep.INDIVIDUAL:
      return (
        <div className="epcs-setup">
          <EpcsIndividualFlow
            assuranceId={flow.assuranceId}
            channels={flow.channels}
            initialState={flow.initialState}
            authenticatorId={routeState.authenticatorId}
            authenticatorShortName={routeState.authenticatorShortName}
            authenticatorDeviceModel={routeState.authenticatorDeviceModel}
            tenantId={tenantId}
            workflowId={workflowIdRef.current}
            onStepChange={setIndividualStep}
            cancelFlow={cancelFlow}
            completeFlow={completeFlow}
          />
        </div>
      );
  }
};

export default EpcsSetup;
